import { HttpClient} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface Language {
  language: string;
  code: string;
  type: string;
  icon: string;
}

@Injectable({ providedIn: 'root' })
export class LocalizationService{
  private wordUrl = 'assets/localizationWords.json';
  wordDictionary = {};
  private _currentLang = new BehaviorSubject<string>('tr');

  // Centralized language configuration
  public readonly availableLanguages: Language[] = [
    {
      language: 'Türkçe',
      code: 'tr',
      type: 'TR',
      icon: '/assets/images/flag/icon-flag-tr.svg',
    },
    {
      language: 'English',
      code: 'en',
      type: 'US',
      icon: '/assets/images/flag/icon-flag-en.svg',
    },
  ];

  get activeLanguage(): string {
    return this._currentLang.value;
  }

  get currentLang(): BehaviorSubject<string> {
    return this._currentLang;
  }

  constructor(public http: HttpClient) {
    const savedLang = sessionStorage.getItem("activeLanguage");

    if (savedLang) {
      this._currentLang.next(savedLang);
    } else {
      sessionStorage.setItem('activeLanguage', 'tr');
      this._currentLang.next('tr');
    }

    // Dil değişikliğini dinle
    this._currentLang.subscribe(lang => {
      sessionStorage.setItem('activeLanguage', lang);
    });
  }

  initConfig() {
    return new Promise<boolean>((resolve, reject) => {
      if(!sessionStorage.getItem("activeLanguage")) {
        sessionStorage.setItem('activeLanguage','tr');
        this._currentLang.next('tr');
      }

      this.getWordJSON().pipe(
        catchError(err => {
          console.error('Error loading word JSON:', err);
          reject(err);
          return of(null);
        })
      ).subscribe(data => {
        if (data) {
          this.wordDictionary = data;
          resolve(true);
        }
      });
    });
  }

  getActiveBrowserLanguage(){
    return this.activeLanguage;
  }

  setCurrentLang(lang: string): void {
    if (lang !== this._currentLang.value) {
      this._currentLang.next(lang);
      sessionStorage.setItem('activeLanguage', lang);
    }
  }

  getSelectedLanguage(): Language {
    return this.availableLanguages.find(lang => lang.code === this.activeLanguage) || this.availableLanguages[0];
  }

  getWordJSON(): Observable<any> {
    return this.http.get(this.wordUrl).pipe(
      catchError(error => {
        return of({});
      })
    );
  }

  getLocalizationValue(key: string): string {
    if (!key) return '';

    try {
      // Kontrol amaçlı log
      if (Object.keys(this.wordDictionary).length === 0) {
        console.warn('Word dictionary is empty! initConfig may not have been called.');
      }

      // Nokta notasyonu ile iç içe anahtarları destekle (örn: "Common.Save")
      const keyParts = key.split('.');
      let currentObj = this.wordDictionary;

      // İç içe nesnelerde gezinme (örn: "Common.Save" için önce Common, sonra Save)
      for (let i = 0; i < keyParts.length; i++) {
        const part = keyParts[i];

        if (!currentObj[part]) {
          return `[${key}]`;
        }

        // Son parçaya ulaşıldıysa ve bu bir lokalizasyon nesnesi ise
        if (i === keyParts.length - 1 && currentObj[part][this.activeLanguage]) {
          return currentObj[part][this.activeLanguage];
        }

        // Bir sonraki iç içe nesneye geç
        currentObj = currentObj[part];
      }

      if (currentObj && currentObj[this.activeLanguage]) {
        return currentObj[this.activeLanguage];
      }
      const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
      if (this.wordDictionary[capitalizedKey] && this.wordDictionary[capitalizedKey][this.activeLanguage]) {
        return this.wordDictionary[capitalizedKey][this.activeLanguage];
      }
      return `[${key}]`;
    } catch (error) {
      return `[${key}]`;
    }
  }
}
